import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button, Spinner } from 'react-bootstrap';
import OpenComplaintButton from '../complaints/OpenComplaintButton';
import { useGuestVoice } from '../../hooks/useGuestReviews';
import { formatShortDate } from '../../utils/dateFormatter';
import { formatCount } from '../../utils/numberFormatter';
import { getPlatformDisplayName } from '../../utils/platformUtils';
import { TEXT, problemLabel, praiseLabel } from '../../config/guestVoiceLabels';
import '../../styles/GuestVoiceTab.css';

/**
 * «صوت الضيف» — what guests love and what needs improving, one unit at a time (tasks/guest-voice-tab.md step 5).
 *
 * Everything shown is the server's: the AI labelled each review once, and grouping, "repeat", «عاد بعد الإصلاح»
 * and the order are plain rules there (G6, G7, G12). This only lays it out, in three tiers that each answer one
 * question — across units: what recurs · per unit: how many problems, in what state, the most important one ·
 * the unit in view: each problem with its mentions and ONE action (G11): open its complaint, or open a complaint
 * from it; nothing is created on its own. No percentages, no charts (G10). The unit in view is kept in the URL
 * (`?unit=`) beside the page's own `list=voice`, like the complaints tab. Core staff only — the page and the API
 * both. Lines from a private note are marked as such (G3). Colour carries one meaning each: red = urgent,
 * orange = repeat, purple = came back after the fix, green = praise; grey = being handled or not urgent.
 */
const MENTIONS_SHOWN = 2;
const CHIPS_SHOWN = 3;
const PRAISE_LINES_SHOWN = 2;

// A unit needs a look while any problem has no open complaint — urgent, repeat, came back or simply unlogged.
const needsLook = (u) => u.problems - (u.handled || 0) > 0;

const UnitTile = ({ summary, active, onSelect }) => {
  const { unit, reviews, problems, repeats, high, cameBack, handled = 0, leadTopicKey } = summary;
  // What still needs a look, in words — a row already being handled adds nothing here (server rule). With no
  // urgent / repeat / came-back problem, the line says the plain fact: all being handled, or how many have no complaint.
  const unlogged = problems - handled;
  const states = [
    high > 0 && { kind: 'high', text: TEXT.stateHigh(high) },
    repeats > 0 && { kind: 'repeat', text: TEXT.stateRepeat(repeats) },
    cameBack > 0 && { kind: 'back', text: TEXT.stateCameBack(cameBack) }
  ].filter(Boolean);
  if (states.length === 0 && problems > 0) {
    states.push(unlogged === 0
      ? { kind: 'handled', text: TEXT.unitHandled }
      : { kind: 'unlogged', text: TEXT.unitUnlogged(unlogged) });
  }
  const tone = problems === 0 ? 'clear' : high ? 'high' : repeats ? 'repeat' : cameBack ? 'back' : 'quiet';
  const lead = leadTopicKey ? problemLabel(leadTopicKey) : null;

  return (
    <button
      type="button"
      className={`gv-unit gv-unit--${tone}${active ? ' gv-unit--active' : ''}`}
      aria-pressed={active}
      onClick={onSelect}
    >
      <span className="gv-unit-head">
        <span className="gv-unit-code"><bdi>{unit.code || '—'}</bdi></span>
        <span className="gv-unit-reviews">{TEXT.reviewsCount(reviews)}</span>
      </span>
      <span className="gv-unit-name">{unit.name}</span>
      <span className="gv-unit-fact">{problems === 0 ? TEXT.unitClear : TEXT.problemsCount(problems)}</span>
      {states.length > 0 && (
        <span className="gv-unit-states">
          {states.map(s => <span key={s.kind} className={`gv-state gv-state--${s.kind}`}>{s.text}</span>)}
        </span>
      )}
      {lead && <span className="gv-unit-lead">{problems === 1 ? lead : TEXT.leadTopic(lead)}</span>}
    </button>
  );
};

const Mention = ({ m }) => (
  <li className="gv-mention">
    <span className="gv-mention-line" dir="auto">{m.noteAr}</span>
    <span className="gv-mention-meta">
      <span>{getPlatformDisplayName(m.platform)}</span>
      <span>{formatShortDate(m.at)}</span>
      {typeof m.score === 'number' && <span>{TEXT.score(m.score)}</span>}
      {m.source === 'private' && (
        <span className="gv-private" title={TEXT.privateNoteHint}>
          <i className="bi bi-lock-fill" aria-hidden="true"></i> {TEXT.privateNote}
        </span>
      )}
    </span>
  </li>
);

const ProblemRow = ({ row }) => {
  const [open, setOpen] = useState(false);
  const shown = open ? row.mentions : row.mentions.slice(0, MENTIONS_SHOWN);
  const hidden = row.mentions.length - MENTIONS_SHOWN;
  // The row's bar says its one most important fact: being handled (grey) > urgent > repeat > came back.
  const tone = row.complaint ? 'handled'
    : row.severity === 'high' ? 'high'
      : row.repeat ? 'repeat'
        : row.cameBackAfterFix ? 'back' : 'plain';

  return (
    <article className={`gv-row gv-row--${tone}`}>
      <div className="gv-row-head">
        <div className="gv-row-title">
          <h4 className="gv-topic">{problemLabel(row.topicKey)}</h4>
          {row.severity === 'high' && <span className="gv-badge gv-badge--high">{TEXT.badgeHigh}</span>}
          {row.repeat && <span className="gv-badge gv-badge--repeat">{TEXT.badgeRepeat}</span>}
          {row.cameBackAfterFix && <span className="gv-badge gv-badge--back">{TEXT.badgeCameBack}</span>}
          {row.complaint && <span className="gv-badge gv-badge--handled">{TEXT.badgeHandled}</span>}
        </div>
        <div className="gv-row-action">
          {row.complaint ? (
            <Link className="gr-link gv-view" to={`/ops/reviews?list=complaints&id=${row.complaint.id}`}>
              {TEXT.viewComplaint}
            </Link>
          ) : row.logFrom ? (
            <OpenComplaintButton variant="link" source={{ kind: 'ota_review', id: row.logFrom.reviewId, topic: row.topicKey }} />
          ) : (
            <span className="gv-muted" title={TEXT.noStayHint}>{TEXT.noStay}</span>
          )}
        </div>
      </div>
      <p className="gv-row-meta">
        <span>{TEXT.stays(row.stays)}</span>
        <span>{TEXT.lastMention(formatShortDate(row.lastAt))}</span>
      </p>
      <ul className="gv-mentions">
        {shown.map((m, i) => <Mention key={`${m.reviewId}-${m.source}-${i}`} m={m} />)}
      </ul>
      {hidden > 0 && (
        <Button variant="link" className="gv-more" aria-expanded={open} onClick={() => setOpen(v => !v)}>
          {open ? TEXT.showLess : TEXT.showMore(hidden)}
        </Button>
      )}
    </article>
  );
};

// The same sentence from several guests is one example, not three — the count already says how many.
const distinctLines = (lines) => {
  const seen = new Set();
  return lines.filter(l => {
    const key = String(l.noteAr || '').trim();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  }).slice(0, PRAISE_LINES_SHOWN);
};

const PraiseRow = ({ row }) => {
  const lines = distinctLines(row.lines);
  return (
    <li className="gv-praise">
      <div className="gv-praise-head">
        <span className="gv-praise-topic">{praiseLabel(row.topicKey)}</span>
        <span className="gv-praise-count"><strong>{formatCount(row.count)}</strong> {TEXT.reviewsWord(row.count)}</span>
      </div>
      {lines.length > 0 && (
        <ul className="gv-praise-lines">
          {lines.map((l, i) => (
            <li key={i} dir="auto">
              {l.noteAr}
              {l.source === 'private' && (
                <span className="gv-private" title={TEXT.privateNoteHint}> <i className="bi bi-lock-fill" aria-hidden="true"></i></span>
              )}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};

const GuestVoiceTab = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const unitParam = searchParams.get('unit');
  const { data, isLoading, isError, refetch, isFetching } = useGuestVoice(unitParam, true);
  // At 50 units the tiles would be 10 rows: only the units that need a look are shown, the rest fold into one line.
  const [showAll, setShowAll] = useState(false);

  // The page's own `list=voice` always stays; only `unit` changes here.
  const pick = (id) => setSearchParams({ list: 'voice', unit: String(id) });

  if (isLoading) return <div className="gr-state"><Spinner animation="border" size="sm" /> {TEXT.loading}</div>;
  if (isError || !data) {
    return (
      <div className="gr-state gr-error" role="alert">
        {TEXT.loadError}{' '}
        <Button variant="link" className="gr-link" onClick={() => refetch()}>{TEXT.retry}</Button>
      </div>
    );
  }

  const { counts, summary, top = [], units, unitId, improve, praise } = data;
  const topPraise = (summary && summary.topPraise) || [];
  // Each side scales to its own longest bar — the count beside it says the measure (units / reviews).
  const maxUnits = Math.max(1, ...top.map(t => t.units.length));
  const maxPraise = Math.max(1, ...topPraise.map(t => t.count));
  // The unit in view always shows among the chips, so picking it from another row never hides it.
  const chipsOf = (list) => [...list].sort((a, b) => isCurrent(b.id) - isCurrent(a.id)).slice(0, CHIPS_SHOWN);
  const current = units.find(u => String(u.unit.id) === String(unitId));
  const isCurrent = (id) => String(id) === String(unitId);
  // The unit in view always stays visible — picking a folded unit from the fleet strip must show its tile.
  const foldable = units.filter(u => !needsLook(u) && !isCurrent(u.unit.id));
  const visibleUnits = showAll ? units : units.filter(u => needsLook(u) || isCurrent(u.unit.id));

  return (
    <div className="gv" aria-busy={isFetching}>
      {/* Across all units (G23–G26): what needs improving and what guests praise, ranked, one thin bar each —
          the bar is the units (problems) or the reviews (praise); the count is the label. Counts, never %. */}
      {counts.reviews > 0 && (
        <section className="gv-fleet" aria-labelledby="gv-fleet-title">
          <div className="gv-fleet-head">
            <h3 id="gv-fleet-title" className="gv-fleet-title">{TEXT.fleetTitle}</h3>
            <span className="gv-fleet-hint">{TEXT.fleetHint}</span>
          </div>
          {/* The same two cards and column split as the unit panels below, so each side reads as one column:
              problems on the start side, praise on the end side, top to bottom. */}
          <div className="gv-panels gv-fleet-sides">
            <div className="gv-panel gv-panel--improve gv-fleet-side">
              <h4 className="gv-panel-title gv-fleet-side-title">{TEXT.improveTitle}</h4>
              {top.length === 0 ? <p className="gv-empty">{TEXT.fleetProblemsEmpty}</p> : (
                <ul className="gv-bars">
                  {top.map(t => (
                    <li key={t.topicKey} className="gv-bar-row">
                      <span className="gv-bar-topic" title={TEXT.fleetProblemTip(problemLabel(t.topicKey), t.units.length, t.stays)}>{problemLabel(t.topicKey)}</span>
                      <span className="gv-bar-track" aria-hidden="true" title={TEXT.fleetProblemTip(problemLabel(t.topicKey), t.units.length, t.stays)}>
                        <span className="gv-bar gv-bar--improve" style={{ inlineSize: `${(t.units.length / maxUnits) * 100}%` }} />
                      </span>
                      <span className="gv-bar-value"><strong>{TEXT.topTimes(t.stays)}</strong> {TEXT.topUnits(t.units.length)}</span>
                      {/* The jump from a fleet pattern to the unit that has it — quiet text codes, the unit in view
                          first and in purple, at most three, the rest behind «+N» (founder, 8 Oct: quieter). */}
                      <span className="gv-top-units">
                        {chipsOf(t.units).map(u => (
                          <button
                            key={u.id}
                            type="button"
                            className={`gv-top-unit${isCurrent(u.id) ? ' gv-top-unit--active' : ''}`}
                            aria-pressed={isCurrent(u.id)}
                            onClick={() => pick(u.id)}
                          >
                            <bdi>{u.code || u.name}</bdi>
                          </button>
                        ))}
                        {t.units.length > CHIPS_SHOWN && (
                          <span className="gv-top-more" title={t.units.slice(CHIPS_SHOWN).map(u => u.code || u.name).join('، ')}>
                            {TEXT.moreUnits(t.units.length - CHIPS_SHOWN)}
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="gv-panel gv-panel--love gv-fleet-side">
              <h4 className="gv-panel-title gv-fleet-side-title">{TEXT.loveTitle}</h4>
              {topPraise.length === 0 ? <p className="gv-empty">{TEXT.fleetPraiseEmpty}</p> : (
                <ul className="gv-bars">
                  {topPraise.map(t => (
                    <li key={t.topicKey} className="gv-bar-row">
                      <span className="gv-bar-topic" title={TEXT.fleetPraiseTip(praiseLabel(t.topicKey), t.count)}>{praiseLabel(t.topicKey)}</span>
                      <span className="gv-bar-track" aria-hidden="true" title={TEXT.fleetPraiseTip(praiseLabel(t.topicKey), t.count)}>
                        <span className="gv-bar gv-bar--love" style={{ inlineSize: `${(t.count / maxPraise) * 100}%` }} />
                      </span>
                      <span className="gv-bar-value"><strong>{formatCount(t.count)}</strong> {TEXT.reviewsWord(t.count)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          {/* Orientation, not analytics (G20): what the read covers and the tiles' total, in words, under the block
              and above the work — never cards, no %, no chart (consultant, 8 Oct). */}
          <p className="gv-status" aria-live="polite">
            <span>{TEXT.readStatus(counts.read, counts.reviews)}</span>
            {counts.notRead > 0 && <span className="gv-status-warn">{TEXT.notRead(counts.notRead)}</span>}
            {counts.failed > 0 && <span className="gv-status-warn">{TEXT.failed(counts.failed)}</span>}
            {summary && units.length > 0 && (
              <>
                <span>{summary.problems === 0 ? TEXT.unitClear : TEXT.problemsCount(summary.problems)}</span>
                {summary.praise > 0 && <span>{TEXT.praiseCount(summary.praise)}</span>}
                {summary.repeats > 0 && <span className="gv-summary-repeat">{TEXT.summaryRepeat(summary.repeats)}</span>}
                {summary.high > 0 && <span className="gv-summary-high">{TEXT.summaryHigh(summary.high)}</span>}
              </>
            )}
          </p>
        </section>
      )}

      {units.length === 0 && (
        <div className="gr-empty">
          <i className="bi bi-chat-heart" aria-hidden="true"></i>
          <p>{TEXT.noUnitsTitle}</p>
          <p className="gr-muted">{TEXT.noUnitsHint}</p>
        </div>
      )}

      {units.length > 0 && (
        <div className="gv-units" role="group" aria-label={TEXT.unitsLabel}>
          {visibleUnits.map(u => (
            <UnitTile key={u.unit.id} summary={u} active={isCurrent(u.unit.id)} onSelect={() => pick(u.unit.id)} />
          ))}
        </div>
      )}
      {foldable.length > 0 && (
        <p className="gv-fold">
          <span>{TEXT.foldedUnits(foldable.length)}</span>
          <Button variant="link" className="gv-more" aria-expanded={showAll} onClick={() => setShowAll(v => !v)}>
            {showAll ? TEXT.foldHide : TEXT.foldShow}
          </Button>
        </p>
      )}

      {current && (
        <div className="gv-panels">
          <section className="gv-panel gv-panel--improve" aria-labelledby="gv-improve-title">
            <h3 id="gv-improve-title" className="gv-panel-title">
              {TEXT.improveTitle}
              {improve.length > 0 && <span className="gv-panel-count">{formatCount(improve.length)}</span>}
            </h3>
            {improve.length > 0 && <p className="gv-panel-hint">{TEXT.improveHint}</p>}
            {improve.length === 0 ? (
              <p className="gv-empty">{TEXT.improveEmpty}</p>
            ) : (
              <div className="gv-rows">
                {improve.map(row => <ProblemRow key={row.topicKey} row={row} />)}
              </div>
            )}
          </section>

          <section className="gv-panel gv-panel--love" aria-labelledby="gv-love-title">
            <h3 id="gv-love-title" className="gv-panel-title">{TEXT.loveTitle}</h3>
            {praise.length === 0 ? (
              <p className="gv-empty">{TEXT.loveEmpty}</p>
            ) : (
              <ul className="gv-praise-list">
                {praise.map(row => <PraiseRow key={row.topicKey} row={row} />)}
              </ul>
            )}
          </section>
        </div>
      )}
    </div>
  );
};

export default GuestVoiceTab;
